import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import type { ChartOptions, TooltipItem } from 'chart.js';
import { Fuel, Download } from 'lucide-react';
import type { DieselPriceRow } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import { getCachedDieselPrices } from '@/services/marketIntelService';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';

ChartJS.register(...registerables);

const LINE_COLOR = '#84cc16'; // rs-green accent

const PADD_INFO: Record<string, string> = {
  'PADD1A': 'PADD 1A (New England): Connecticut, Maine, Massachusetts, New Hampshire, Rhode Island, Vermont.',
  'PADD1B': 'PADD 1B (Central Atlantic): Delaware, District of Columbia, Maryland, New Jersey, New York, Pennsylvania.',
  'PADD1C': 'PADD 1C (Lower Atlantic): Florida, Georgia, North Carolina, South Carolina, Virginia, West Virginia.',
  'PADD2': 'PADD 2 (Midwest): Illinois, Indiana, Iowa, Kansas, Kentucky, Michigan, Minnesota, Missouri, Nebraska, North Dakota, Ohio, Oklahoma, South Dakota, Tennessee, Wisconsin.',
  'PADD3': 'PADD 3 (Gulf Coast): Alabama, Arkansas, Louisiana, Mississippi, New Mexico, Texas.',
  'PADD4': 'PADD 4 (Rocky Mountain): Colorado, Idaho, Montana, Utah, Wyoming.',
  'PADD5': 'PADD 5 (West Coast): Alaska (North Slope and Other Mainland), Arizona, California, Hawaii, Nevada, Oregon, Washington.',
};

type RegionKey = Exclude<keyof DieselPriceRow, 'date'>;
type ChartRange = '3M' | '1Y' | '5Y' | 'ALL';

const REGION_COLUMNS: { key: RegionKey; label: string; padd?: string }[] = [
  { key: 'US', label: 'U.S.' },
  { key: 'East_Coast', label: 'East Coast', padd: 'PADD1' },
  { key: 'New_England', label: 'New England', padd: 'PADD1A' },
  { key: 'Central_Atlantic', label: 'Central Atlantic', padd: 'PADD1B' },
  { key: 'Lower_Atlantic', label: 'Lower Atlantic', padd: 'PADD1C' },
  { key: 'Midwest', label: 'Midwest', padd: 'PADD2' },
  { key: 'Gulf_Coast', label: 'Gulf Coast', padd: 'PADD3' },
  { key: 'Rocky_Mountain', label: 'Rocky Mountain', padd: 'PADD4' },
  { key: 'West_Coast', label: 'West Coast', padd: 'PADD5' },
  { key: 'West_Coast_Excl_California', label: 'West Coast Excl. California' },
  { key: 'California', label: 'California' },
];

const fmtPrice = (v: number | null | undefined) =>
  v != null && !Number.isNaN(Number(v)) ? `$${Number(v).toFixed(3)}` : '—';

const fmtValue = (v: number | null | undefined) =>
  v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(3) : '—';

const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const fmtShortDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${mm}/${dd}/${yy}`;
};

const numOrNull = (v: number | null | undefined) =>
  v != null && !Number.isNaN(Number(v)) ? Number(v) : null;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtAxis = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
};

const DieselDataView: React.FC = () => {
  const { isDark } = useTheme();
  const [rows, setRows] = useState<DieselPriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartRegion, setChartRegion] = useState<RegionKey>('US');
  const [chartRange, setChartRange] = useState<ChartRange>('1Y');
  const [historyDateFrom, setHistoryDateFrom] = useState<string>('');
  const [historyDateTo, setHistoryDateTo] = useState<string>('');
  const [historySortDir, setHistorySortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setLoading(true);
    getCachedDieselPrices()
      .then(data => setRows(data))
      .catch(() => setError('Failed to load diesel price data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (rows.length > 0) {
      const sorted = [...rows].sort((a, b) => (a.date < b.date ? 1 : -1));
      setHistoryDateFrom(sorted[sorted.length - 1].date);
      setHistoryDateTo(sorted[0].date);
    }
  }, [rows]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [rows],
  );

  // EIA-style snapshot: latest 3 weeks + change vs week/year/2-year ago.
  const snapshot = useMemo(() => {
    if (sorted.length === 0) return null;
    const latest = sorted[0];
    const recentWeeks = sorted.slice(0, 10).reverse(); // oldest → newest for column order
    const parse = (iso: string) => new Date(iso + 'T00:00:00').getTime();
    const latestTime = parse(latest.date);

    // Closest weekly row to a target date, only if within ~6 weeks of it.
    const closestTo = (targetTime: number): DieselPriceRow | null => {
      let best: DieselPriceRow | null = null;
      let bestDiff = Infinity;
      for (const r of sorted) {
        const diff = Math.abs(parse(r.date) - targetTime);
        if (diff < bestDiff) { bestDiff = diff; best = r; }
      }
      return best && bestDiff <= 45 * 24 * 3600 * 1000 ? best : null;
    };

    const yearAgoTarget = new Date(latestTime); yearAgoTarget.setFullYear(yearAgoTarget.getFullYear() - 1);
    const twoYearAgoTarget = new Date(latestTime); twoYearAgoTarget.setFullYear(twoYearAgoTarget.getFullYear() - 2);

    return {
      latest,
      recentWeeks,
      weekAgo: sorted[1] ?? null,
      yearAgo: closestTo(yearAgoTarget.getTime()),
      twoYearAgo: closestTo(twoYearAgoTarget.getTime()),
    };
  }, [sorted]);

  // KPI tiles: U.S. average, week/week + year/year change, highest & lowest region.
  const kpis = useMemo(() => {
    if (!snapshot) return null;
    const { latest, weekAgo, yearAgo } = snapshot;
    const us = numOrNull(latest.US);
    const weekAgoPrice = weekAgo ? numOrNull(weekAgo.US) : null;
    const yearAgoPrice = yearAgo ? numOrNull(yearAgo.US) : null;
    const regionVals = REGION_COLUMNS.filter(c => c.key !== 'US')
      .map(c => ({ label: c.label, v: numOrNull(latest[c.key]) }))
      .filter((x): x is { label: string; v: number } => x.v != null);
    const weekOverWeekChange = us != null && weekAgoPrice != null ? us - weekAgoPrice : null;
    const yearOverYearChange = us != null && yearAgoPrice != null ? us - yearAgoPrice : null;
    return {
      us,
      weekOverWeekChange,
      weekOverWeekPercent: weekOverWeekChange != null && weekAgoPrice != null && weekAgoPrice !== 0 ? (weekOverWeekChange / weekAgoPrice) * 100 : null,
      yearOverYearChange,
      yearOverYearPercent: yearOverYearChange != null && yearAgoPrice != null && yearAgoPrice !== 0 ? (yearOverYearChange / yearAgoPrice) * 100 : null,
      highest: regionVals.length ? regionVals.reduce((a, b) => (b.v > a.v ? b : a)) : null,
      lowest: regionVals.length ? regionVals.reduce((a, b) => (b.v < a.v ? b : a)) : null,
    };
  }, [snapshot]);

  // Trend chart series for the selected region + time range.
  const chart = useMemo(() => {
    if (sorted.length === 0) return { labels: [] as string[], values: [] as (number | null)[] };
    const asc = [...sorted].reverse();
    const latestTime = new Date(asc[asc.length - 1].date + 'T00:00:00').getTime();
    let cutoff = 0;
    if (chartRange !== 'ALL') {
      const d = new Date(latestTime);
      if (chartRange === '3M') d.setMonth(d.getMonth() - 3);
      else if (chartRange === '1Y') d.setFullYear(d.getFullYear() - 1);
      else if (chartRange === '5Y') d.setFullYear(d.getFullYear() - 5);
      cutoff = d.getTime();
    }
    const filtered = asc.filter(r => new Date(r.date + 'T00:00:00').getTime() >= cutoff);
    return {
      labels: filtered.map(r => r.date),
      values: filtered.map(r => numOrNull(r[chartRegion])),
    };
  }, [sorted, chartRegion, chartRange]);

  // Min/max across the snapshot price cells, for heatmap shading.
  const heat = useMemo(() => {
    if (!snapshot) return null;
    const vals: number[] = [];
    for (const col of REGION_COLUMNS) {
      for (const w of snapshot.recentWeeks) {
        const v = numOrNull(w[col.key]);
        if (v != null) vals.push(v);
      }
    }
    return vals.length ? { min: Math.min(...vals), max: Math.max(...vals) } : null;
  }, [snapshot]);

  const filteredHistory = useMemo(() => {
    let filtered = [...sorted];
    if (historyDateFrom) {
      filtered = filtered.filter(row => row.date >= historyDateFrom);
    }
    if (historyDateTo) {
      filtered = filtered.filter(row => row.date <= historyDateTo);
    }
    if (historySortDir === 'asc') {
      filtered.sort((a, b) => a.date.localeCompare(b.date));
    }
    return filtered;
  }, [sorted, historyDateFrom, historyDateTo, historySortDir]);

  const textPrimary = isDark ? '#f3f4f6' : '#1e2019';
  const textMuted = isDark ? 'rgba(243,244,246,0.5)' : 'rgba(30,32,25,0.5)';
  const headerBg = isDark ? 'rgba(30,32,25,0.95)' : '#eef1ec';
  const rowBorder = isDark ? 'rgba(61,61,61,0.4)' : '#d7dad3';
  const cardBg = isDark ? 'rgba(26,28,24,0.6)' : '#ffffff';
  const cardBorder = isDark ? 'rgba(61,61,61,0.5)' : '#dbded7';
  const headerText = isDark ? textMuted : '#000000';
  const histRowEven = isDark ? '#141613' : '#ffffff';
  const histRowOdd = isDark ? '#1c1e1a' : '#f5f7f3';

  const tickColor = isDark ? '#e9ede6' : '#1e2019';
  const gridColor = isDark ? '#566151' : '#d0d3ce';
  const tooltipBg = isDark ? '#2a2e28' : '#f0f2ee';

  const CHANGE_UP = '#dc2626';   // price rose (cost up) — red
  const CHANGE_DOWN = '#16a34a'; // price fell (cost down) — green

  // Change of `latest` vs a prior row for one region, rendered with a colored arrow.
  const renderChange = (latest: DieselPriceRow, prior: DieselPriceRow | null, key: RegionKey) => {
    const a = numOrNull(latest[key]);
    const b = prior ? numOrNull(prior[key]) : null;
    if (a == null || b == null) return <span style={{ color: textMuted }}>—</span>;
    const change = a - b;
    if (Math.abs(change) < 0.0005) return <span style={{ color: textMuted }}>0.000</span>;
    const up = change > 0;
    return (
      <span style={{ color: up ? CHANGE_UP : CHANGE_DOWN }} className="tabular-nums">
        {up ? '▲' : '▼'} {Math.abs(change).toFixed(3)}
      </span>
    );
  };

  // Heatmap tint for a snapshot price cell: green (low) → red (high).
  const heatColor = (v: number | null) => {
    if (v == null || !heat || heat.max === heat.min) return undefined;
    const t = (v - heat.min) / (heat.max - heat.min);
    return `hsla(${120 * (1 - t)}, 65%, 45%, ${isDark ? 0.3 : 0.16})`;
  };

  // Large signed-change display for the KPI tiles.
  const bigChange = (v: number | null) => {
    if (v == null) return <span style={{ color: textMuted }}>—</span>;
    if (Math.abs(v) < 0.0005) return <span style={{ color: textMuted }}>$0.000</span>;
    const up = v > 0;
    return (
      <span style={{ color: up ? CHANGE_UP : CHANGE_DOWN }}>
        {up ? '▲' : '▼'} ${Math.abs(v).toFixed(3)}
      </span>
    );
  };

  const statCard = (title: string, value: React.ReactNode, sub?: React.ReactNode) => (
    <div className="rounded-xl border p-4 flex flex-col gap-1" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
      <span className="text-[11px] uppercase tracking-wider" style={{ color: textMuted }}>{title}</span>
      <span className="text-2xl font-semibold tabular-nums" style={{ color: textPrimary }}>{value}</span>
      {sub && <span className="text-xs truncate" style={{ color: textMuted }}>{sub}</span>}
    </div>
  );

  const downloadCsv = () => {
    const header = ['Week Ending', ...REGION_COLUMNS.map(c => c.label)];
    const lines = sorted.map(r =>
      [r.date, ...REGION_COLUMNS.map(c => { const v = numOrNull(r[c.key]); return v != null ? v.toFixed(3) : ''; })].join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diesel_prices_history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(), []);

  const lineData = {
    labels: chart.labels,
    datasets: [{
      label: REGION_COLUMNS.find(c => c.key === chartRegion)?.label ?? 'Price',
      data: chart.values,
      borderColor: LINE_COLOR,
      backgroundColor: 'rgba(132,204,22,0.10)',
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.15,
      spanGaps: true,
      fill: true,
    }],
  };

  const lineOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tickColor,
        bodyColor: tickColor,
        borderColor: gridColor,
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => (items.length ? fmtDate(String(items[0].label)) : ''),
          label: (item: TooltipItem<'line'>) => `  $${item.parsed.y != null ? item.parsed.y.toFixed(3) : 'N/A'}/gal`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: tickColor,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          callback: (_v: string | number, index: number) => {
            const l = chart.labels[index];
            return l ? fmtAxis(l) : '';
          },
        },
        grid: { color: gridColor },
      },
      y: {
        ticks: { color: tickColor, callback: (v: string | number) => `$${Number(v).toFixed(2)}` },
        grid: { color: gridColor },
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="animate-pulse text-rs-off-white/60">Loading diesel data…</div>
      </div>
    );
  }

  if (error || sorted.length === 0) {
    return (
      <div className="flex items-center justify-center h-80">
        <p className="text-rs-off-white/60">{error || 'No diesel price data available.'}</p>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Fuel className="w-6 h-6 text-rs-green" />
        <div>
          <h2 className="text-2xl font-light" style={{ color: textPrimary }}>
            Weekly Diesel Retail Prices
          </h2>
          <p className="text-sm" style={{ color: textMuted }}>
            No. 2 Diesel Ultra Low Sulfur (0–15 ppm), USD per gallon
          </p>
        </div>
      </div>

      {snapshot && (
        <span
          className="inline-flex items-center gap-2 self-start rounded-full px-3 py-1 text-xs font-medium"
          style={{ backgroundColor: headerBg, color: textPrimary, border: `1px solid ${cardBorder}` }}
        >
          <span className="w-2 h-2 rounded-full bg-rs-green" />
          Data through {fmtDate(snapshot.latest.date)}
        </span>
      )}

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-2">
          {statCard(
            'U.S. Average',
            <div className="flex items-center justify-between flex-1 gap-4">
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                {fmtPrice(kpis.us)}
              </div>
              <div className="flex flex-col items-end gap-1" style={{ fontSize: '0.875rem' }}>
                <div>
                  {kpis.weekOverWeekChange != null ? (
                    <>
                      {bigChange(kpis.weekOverWeekChange)} from last week
                    </>
                  ) : (
                    '—'
                  )}
                </div>
                <div style={{ opacity: 0.7 }}>
                  {snapshot?.weekAgo ? `(was ${fmtPrice(numOrNull(snapshot.weekAgo.US))})` : ''}
                </div>
              </div>
            </div>,
            'per gallon, this week'
          )}
          {statCard(
            'Week / Week',
            <div className="flex items-center justify-between flex-1">
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {bigChange(kpis.weekOverWeekChange)}
              </div>
              <div className="flex flex-col items-end gap-1">
                <div style={{ fontSize: '1.125rem', fontWeight: 500 }}>
                  {kpis.weekOverWeekPercent != null
                    ? `${kpis.weekOverWeekPercent >= 0 ? '+' : ''}${kpis.weekOverWeekPercent.toFixed(1)}%`
                    : '—'}
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>vs. prior week</div>
              </div>
            </div>,
            ''
          )}
          {statCard(
            'Year / Year',
            <div className="flex items-center justify-between flex-1">
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {bigChange(kpis.yearOverYearChange)}
              </div>
              <div className="flex flex-col items-end gap-1">
                <div style={{ fontSize: '1.125rem', fontWeight: 500 }}>
                  {kpis.yearOverYearPercent != null
                    ? `${kpis.yearOverYearPercent >= 0 ? '+' : ''}${kpis.yearOverYearPercent.toFixed(1)}%`
                    : '—'}
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>vs. 1 year ago</div>
              </div>
            </div>,
            ''
          )}
          {statCard(
            'Highest Region',
            <div className="flex items-center justify-between flex-1">
              <div style={{ fontSize: '1rem', fontWeight: 500 }}>
                {kpis.highest?.label || '—'}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'right' }}>
                {fmtPrice(kpis.highest?.v)}
              </div>
            </div>,
            ''
          )}
          {statCard(
            'Lowest Region',
            <div className="flex items-center justify-between flex-1">
              <div style={{ fontSize: '1rem', fontWeight: 500 }}>
                {kpis.lowest?.label || '—'}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'right' }}>
                {fmtPrice(kpis.lowest?.v)}
              </div>
            </div>,
            ''
          )}
        </div>
      )}

      <h3 className="text-sm font-medium uppercase tracking-wider mt-2" style={{ color: headerText }}>
        Price Trend
      </h3>

      <div className="rounded-xl border shadow-lg p-5" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={chartRegion}
            onChange={e => setChartRegion(e.target.value as RegionKey)}
            className="bg-rs-jet border border-rs-jet-light/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rs-green"
            style={{ color: textPrimary }}
          >
            {REGION_COLUMNS.map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <div className="flex gap-1">
            {(['3M', '1Y', '5Y', 'ALL'] as ChartRange[]).map(r => {
              const active = chartRange === r;
              return (
                <button
                  key={r}
                  onClick={() => setChartRange(r)}
                  className="px-3 py-1 rounded text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: active ? LINE_COLOR : 'transparent',
                    color: active ? '#000' : textMuted,
                    border: `1px solid ${active ? LINE_COLOR : cardBorder}`,
                  }}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
        <div className="h-[360px]">
          <Line data={lineData} options={lineOptions} plugins={[hoverLinePlugin]} />
        </div>
      </div>

      <h3 className="text-sm font-medium uppercase tracking-wider mt-2" style={{ color: headerText }}>
        Recent Prices &amp; Changes
      </h3>

      {snapshot && (
        <div
          className="backdrop-blur-sm border rounded-xl shadow-lg overflow-hidden"
          style={{ backgroundColor: cardBg, borderColor: cardBorder }}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    className="text-left font-medium uppercase tracking-wider px-4 py-3 whitespace-nowrap align-bottom"
                    style={{ backgroundColor: headerBg, color: headerText, borderBottom: `1px solid ${rowBorder}` }}
                  >
                    Region
                  </th>
                  {snapshot.recentWeeks.map(w => (
                    <th
                      key={w.date}
                      rowSpan={2}
                      className="text-right font-medium px-4 py-3 whitespace-nowrap align-bottom tabular-nums"
                      style={{ backgroundColor: headerBg, color: headerText, borderBottom: `1px solid ${rowBorder}` }}
                    >
                      {fmtShortDate(w.date)}
                    </th>
                  ))}
                  <th
                    colSpan={3}
                    className="text-center font-medium uppercase tracking-wider px-4 pt-3 pb-1 whitespace-nowrap"
                    style={{ backgroundColor: headerBg, color: headerText, borderLeft: `1px solid ${rowBorder}` }}
                  >
                    Change from
                  </th>
                </tr>
                <tr>
                  {['2 year ago', 'year ago', 'week ago'].map((lbl, i) => (
                    <th
                      key={lbl}
                      className="text-right font-medium px-4 pb-3 whitespace-nowrap"
                      style={{ backgroundColor: headerBg, color: headerText, borderBottom: `1px solid ${rowBorder}`, borderLeft: i === 0 ? `1px solid ${rowBorder}` : undefined }}
                    >
                      {lbl}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {REGION_COLUMNS.map((col, i) => (
                  <tr
                    key={col.key}
                    style={{ backgroundColor: i % 2 === 0 ? histRowEven : histRowOdd }}
                  >
                    <td className="px-4 py-2" style={{ borderBottom: `1px solid ${rowBorder}` }}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium" style={{ color: textPrimary }}>{col.label}</span>
                        {col.padd && PADD_INFO[col.padd] ? (
                          <div className="relative group">
                            <span className="text-xs cursor-help hover:underline" style={{ color: textMuted }}>
                              {col.padd}
                            </span>
                            <div className="invisible group-hover:visible absolute bottom-full right-0 mb-2 w-72 p-3 rounded-lg text-xs z-50 shadow-lg" style={{ backgroundColor: cardBg, borderColor: cardBorder, border: `1px solid ${cardBorder}`, color: textPrimary }}>
                              {PADD_INFO[col.padd]}
                            </div>
                          </div>
                        ) : col.padd ? (
                          <span className="text-xs" style={{ color: textMuted }}>{col.padd}</span>
                        ) : null}
                      </div>
                    </td>
                    {snapshot.recentWeeks.map(w => (
                      <td
                        key={w.date}
                        className="text-right px-4 py-2 whitespace-nowrap tabular-nums"
                        style={{ color: textPrimary, borderBottom: `1px solid ${rowBorder}`, backgroundColor: heatColor(numOrNull(w[col.key])) }}
                      >
                        {fmtValue(w[col.key])}
                      </td>
                    ))}
                    <td className="text-right px-4 py-2 whitespace-nowrap" style={{ borderBottom: `1px solid ${rowBorder}`, borderLeft: `1px solid ${rowBorder}` }}>
                      {renderChange(snapshot.latest, snapshot.twoYearAgo, col.key)}
                    </td>
                    <td className="text-right px-4 py-2 whitespace-nowrap" style={{ borderBottom: `1px solid ${rowBorder}` }}>
                      {renderChange(snapshot.latest, snapshot.yearAgo, col.key)}
                    </td>
                    <td className="text-right px-4 py-2 whitespace-nowrap" style={{ borderBottom: `1px solid ${rowBorder}` }}>
                      {renderChange(snapshot.latest, snapshot.weekAgo, col.key)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-xs italic" style={{ color: textMuted }}>*taxes included</p>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-2">
        <h3 className="text-sm font-medium uppercase tracking-wider" style={{ color: headerText }}>
          Full History
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          {/* Tint the native calendar-picker icon to match the input text color (theme-aware). */}
          <style>{`
            .diesel-date-input::-webkit-calendar-picker-indicator {
              cursor: pointer;
              filter: ${isDark ? 'invert(1) brightness(0.95)' : 'none'};
              opacity: 1 !important;
              display: block !important;
            }
            .diesel-date-input[readonly]::-webkit-calendar-picker-indicator {
              cursor: pointer;
              pointer-events: auto;
            }
          `}</style>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: headerText }}>From:</label>
            <input
              type="date"
              value={historyDateFrom}
              onChange={(e) => {
                const val = e.target.value.slice(0, 10);
                setHistoryDateFrom(val);
              }}
              onKeyDown={(e) => {
                e.preventDefault();
              }}
              min={sorted.length > 0 ? sorted[sorted.length - 1].date : ''}
              max={sorted.length > 0 ? sorted[0].date : ''}
              placeholder={sorted.length > 0 ? sorted[sorted.length - 1].date : ''}
              className="diesel-date-input bg-rs-jet border border-rs-jet-light/50 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-rs-green cursor-pointer"
              style={{ color: textPrimary }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: headerText }}>To:</label>
            <input
              type="date"
              value={historyDateTo}
              onChange={(e) => {
                const val = e.target.value.slice(0, 10);
                setHistoryDateTo(val);
              }}
              onKeyDown={(e) => {
                e.preventDefault();
              }}
              min={sorted.length > 0 ? sorted[sorted.length - 1].date : ''}
              max={sorted.length > 0 ? sorted[0].date : ''}
              placeholder={sorted.length > 0 ? sorted[0].date : ''}
              className="diesel-date-input bg-rs-jet border border-rs-jet-light/50 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-rs-green cursor-pointer"
              style={{ color: textPrimary }}
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setHistorySortDir('asc')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                historySortDir === 'asc'
                  ? 'bg-rs-green text-black'
                  : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
              title="Ascending"
            >
              ↑ Asc
            </button>
            <button
              onClick={() => setHistorySortDir('desc')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                historySortDir === 'desc'
                  ? 'bg-rs-green text-black'
                  : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
              title="Descending"
            >
              ↓ Desc
            </button>
          </div>
          <button
            onClick={downloadCsv}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded text-xs font-medium bg-rs-green text-black hover:bg-rs-green/80 transition-colors ml-auto"
            title="Download Full History CSV"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      <div
        className="backdrop-blur-sm border rounded-xl shadow-lg overflow-hidden"
        style={{ backgroundColor: cardBg, borderColor: cardBorder }}
      >
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th
                  className="sticky top-0 left-0 z-20 text-left font-medium uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                  style={{ backgroundColor: headerBg, color: headerText, borderBottom: `1px solid ${rowBorder}` }}
                >
                  Week Ending
                </th>
                {REGION_COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="sticky top-0 z-10 text-right font-medium uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                    style={{ backgroundColor: headerBg, color: headerText, borderBottom: `1px solid ${rowBorder}` }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((row, i) => (
                <tr
                  key={row.date}
                  className={`group ${isDark ? 'hover:!bg-rs-dark-green' : 'hover:!bg-rs-green'}`}
                  style={{ backgroundColor: i % 2 === 0 ? histRowEven : histRowOdd }}
                >
                  <td
                    className={`sticky left-0 z-10 text-left px-4 py-2 whitespace-nowrap font-medium ${isDark ? 'group-hover:!bg-rs-dark-green' : 'group-hover:!bg-rs-green'}`}
                    style={{
                      color: textPrimary,
                      backgroundColor: i % 2 === 0 ? histRowEven : histRowOdd,
                      borderBottom: `1px solid ${rowBorder}`,
                    }}
                  >
                    {fmtDate(row.date)}
                  </td>
                  {REGION_COLUMNS.map(col => (
                    <td
                      key={col.key}
                      className="text-right px-4 py-2 whitespace-nowrap tabular-nums"
                      style={{ color: textPrimary, borderBottom: `1px solid ${rowBorder}` }}
                    >
                      {fmtPrice(row[col.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DieselDataView;
